'use client';!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:offsoo-landing":true});}catch(e){}}();

import { clsx } from 'clsx/lite'

import { AppUiVisual } from '../app-ui-visual'
import { client, CLIENT_BUDGETS, CLIENT_COUNT, OVER_BUDGET } from '../demo-data'
import { useVisualCopy, type VisualCopy } from '../visual-copy'
import { AppHeader, AppScreen, AppSubHeader, HeaderStat } from './app-screen'
import styles from './budget-visual.module.css'
import { useScenario } from './use-scenario'

/**
 * Sorted the way the budget report itself sorts: heaviest consumption first, so the clients that
 * need attention are the ones you read first — and the two that cross their budget do so at the top
 * of the list rather than somewhere in the middle of it.
 */
const ROWS = [...CLIENT_BUDGETS].sort((a, b) => (b.overrunPct ?? b.pct) - (a.overrunPct ?? a.pct))

/**
 * The book year a client's budget runs for, where one is running. A fee budget is agreed per year,
 * so the year is what a budget row can usefully carry — and not every client has one open, which is
 * why the column is sparse here as it is in the report.
 */
const BUDGET_YEAR: Partial<Record<string, string>> = {
  '10020': '2025',
  '10010': '2026',
  '10027': '2025',
  '10044': '2026',
}

/** The clients whose consumption climbs past their budget mid-loop, and the beat each crosses on. */
const SPIKE_AT: Record<string, number> = Object.fromEntries(OVER_BUDGET.map((row, index) => [row.nr, 4 + index * 2]))

const columns = (t: VisualCopy) => [
  { label: t.word.hours.toUpperCase(), align: 'right' as const, className: styles.hideNarrow },
  { label: t.word.budget.toUpperCase(), align: 'right' as const },
  { label: t.word.used.toUpperCase(), align: 'center' as const, className: styles.hideNarrow },
]

const TOTAL_BEATS = 11
const REST_BEAT = 8 // SSR / reduced-motion snapshot — all bars grown, both overruns flagged
const RESET_BEAT = 10 // final loop beat: already reads as the sparse starting state
const GROW_FROM = 1

/** Resolves `beat` (SSR/reduced-motion `null` → filled rest) to the beat used to derive bar state. */
function effectiveBeat(beat: number | null): number {
  if (beat === null) return REST_BEAT
  return beat >= RESET_BEAT ? 0 : beat
}

/** >100% = over budget, >=90% = nearly there, else on track. Same three tones as the boards. */
function tone(pct: number): 'over' | 'near' | 'ok' {
  if (pct > 100) return 'over'
  return pct >= 90 ? 'near' : 'ok'
}

const TONE_COLOR = {
  over: 'var(--au-late)',
  near: 'var(--au-sig)',
  ok: 'var(--au-done)',
} as const

/**
 * Budgetten — the fee-budget report: per client the hours written, the agreed budget and how far
 * that budget has been eaten into, as a bar with the percentage in it. The bars grow in, then two
 * clients climb past 100% and flag red, which is what the card is about. Modelled on the app's
 * Rapportages → Budgetten → Klanten screen. Driven by `useScenario`'s 11-beat loop; `beat === null`
 * (SSR, no-JS, reduced motion) renders the resolved rest state: every bar grown, both overruns red.
 */
export function BudgetVisual({ className }: { className?: string }) {
  const { t, fmt } = useVisualCopy()
  const { ref, beat } = useScenario(TOTAL_BEATS, { intervalMs: 950, restBeat: REST_BEAT })
  const effBeat = effectiveBeat(beat)
  const grown = effBeat >= GROW_FROM
  /** What each row reads right now — the footer counts these, not the end state it is heading for. */
  const shownPct = (row: (typeof ROWS)[number]) => {
    const spikeAt = SPIKE_AT[row.nr]
    const pct = spikeAt !== undefined && effBeat >= spikeAt ? (row.overrunPct ?? row.pct) : row.pct
    // Before the bars grow they sit at a third of their value: the row is already a row, it just
    // has not been filled in yet.
    return grown ? pct : Math.round(pct * 0.3)
  }
  const over = ROWS.filter((row) => shownPct(row) > 100).length

  return (
    <AppUiVisual label={t.screen.budget.caption} className={className}>
      <AppScreen>
        <div ref={ref} data-visual="budget" className={clsx(styles.root, 'flex h-full flex-col')}>
          <AppHeader
            title={t.screen.budget.title}
            right={<HeaderStat value={CLIENT_COUNT} label={t.word.clientsLower} />}
          />

          <AppSubHeader rowLabel={t.word.client.toUpperCase()} columns={columns(t)} className={styles.head} />

          <div className={styles.rows}>
            {ROWS.map((row) => {
              const { short } = client(row.nr)
              const shown = shownPct(row)
              const rowTone = grown ? tone(shown) : 'ok'

              return (
                <div key={row.nr} className={styles.row}>
                  {/* The same row the client list draws: number, name, legal form — so a client is
                      recognisable as the same client wherever the site shows one. */}
                  <div className={styles.name}>
                    <span className={styles.nr}>{row.nr}</span>
                    <span className={styles.client}>{short}</span>
                    {/* The book year, where the budget is for one — a fee budget is agreed per
                        year, and which year it is says more here than a legal form the client list
                        already carries. Not every client has one running, so not every row shows
                        it, which is also how the report reads. */}
                    {BUDGET_YEAR[row.nr] !== undefined && <span className={styles.year}>{BUDGET_YEAR[row.nr]}</span>}
                  </div>

                  <span className={styles.hours}>{row.hours}</span>
                  <span className={styles.budget}>{fmt.euro(row.budget)}</span>

                  {/* The figure sits beside the bar, not inside it. Centred in the track it landed
                      on the fill's edge at around half — white on colour for one half of the digits
                      and white on grey for the other — so it was exactly the middling values, the
                      ones worth reading, that were unreadable. The capacity chart already places
                      its numbers outside; this now matches it. */}
                  <div className={styles.meter}>
                    <div className={styles.track}>
                      <div
                        className={styles.fill}
                        style={{ width: `${Math.min(shown, 100)}%`, background: TONE_COLOR[rowTone] }}
                      />
                    </div>
                    <span className={styles.pct} style={{ color: TONE_COLOR[rowTone] }}>
                      {shown}%
                    </span>
                  </div>
                </div>
              )
            })}
          </div>

          {/* The count follows the bars rather than announcing where they are heading, so the flag
              turns on at the moment a client actually crosses its budget. */}
          <div className={styles.footer}>
            <span className={clsx(styles.flag, over === 0 && styles.flagOff)}>
              <span className={styles.dot} />
              {t.screen.budget.overBudget(over)}
            </span>
            <span>{t.screen.budget.sortedByUse}</span>
          </div>
        </div>
      </AppScreen>
    </AppUiVisual>
  )
}
